import api, { unwrap } from './client'
import type {
  AttachProjectRepositoryPayload,
  ClaudeMdSuggestion,
  GithubRepositorySettings,
  LinearTeamsFetch,
  PaginatedProjects,
  Project,
  ProjectPayload,
  ProjectRepository,
  UpdateProjectAutoFixSettingsPayload,
  UpdateProjectRepositoryPayload,
} from '@/types/models'

export const projectsApi = {
  // Queues a Linear teams fetch and returns immediately with status
  // 'pending' — poll getLinearTeamsFetch(id) for the result.
  startLinearTeamsFetch(force = false): Promise<LinearTeamsFetch> {
    return unwrap(
      api.get<{ data: LinearTeamsFetch }>('/projects/linear-teams', {
        params: force ? { refresh: 1 } : undefined,
      }),
    )
  },

  getLinearTeamsFetch(id: string): Promise<LinearTeamsFetch> {
    return unwrap(api.get<{ data: LinearTeamsFetch }>(`/projects/linear-teams-fetches/${id}`))
  },

  async listProjects(page?: number): Promise<PaginatedProjects> {
    const params: Record<string, string | number> = {}
    if (page) params.page = page

    const { data } = await api.get<PaginatedProjects>('/projects', { params })

    return data
  },

  getProject(id: string): Promise<Project> {
    return unwrap(api.get<{ data: Project }>(`/projects/${id}`))
  },

  createProject(payload: ProjectPayload): Promise<Project> {
    return unwrap(api.post<{ data: Project }>('/projects', payload))
  },

  updateProject(id: string, payload: Partial<ProjectPayload>): Promise<Project> {
    return unwrap(api.patch<{ data: Project }>(`/projects/${id}`, payload))
  },

  deleteProject(id: string): Promise<unknown> {
    return api.delete(`/projects/${id}`)
  },

  attachRepository(
    projectId: string,
    payload: AttachProjectRepositoryPayload,
  ): Promise<ProjectRepository> {
    return unwrap(api.post<{ data: ProjectRepository }>(repositoriesBase(projectId), payload))
  },

  updateRepository(
    projectId: string,
    projectRepositoryId: string,
    payload: UpdateProjectRepositoryPayload,
  ): Promise<ProjectRepository> {
    return unwrap(
      api.patch<{ data: ProjectRepository }>(
        `${repositoriesBase(projectId)}/${projectRepositoryId}`,
        payload,
      ),
    )
  },

  detachRepository(projectId: string, projectRepositoryId: string): Promise<unknown> {
    return api.delete(`${repositoriesBase(projectId)}/${projectRepositoryId}`)
  },

  // Bulk-applies the auto-fix-comments toggle and attempt cap across every
  // repository this project has attached — there is no project-level
  // settings row; the response is the (per-repo) GithubRepositorySettings
  // this call touched.
  updateAutoFixSettings(
    projectId: string,
    payload: UpdateProjectAutoFixSettingsPayload,
  ): Promise<GithubRepositorySettings[]> {
    return unwrap(
      api.patch<{ data: GithubRepositorySettings[] }>(
        `/projects/${projectId}/auto-fix-settings`,
        payload,
      ),
    )
  },

  // Queues a DraftClaudeMdSuggestionJob and returns immediately with status
  // 'pending' — poll getClaudeMdSuggestion(projectId, id) for the result.
  requestClaudeMdSuggestion(projectId: string): Promise<ClaudeMdSuggestion> {
    return unwrap(
      api.post<{ data: ClaudeMdSuggestion }>(claudeMdSuggestionsBase(projectId), {}),
    )
  },

  getClaudeMdSuggestion(projectId: string, suggestionId: string): Promise<ClaudeMdSuggestion> {
    return unwrap(
      api.get<{ data: ClaudeMdSuggestion }>(`${claudeMdSuggestionsBase(projectId)}/${suggestionId}`),
    )
  },
}

function repositoriesBase(projectId: string): string {
  return `/projects/${projectId}/repositories`
}

function claudeMdSuggestionsBase(projectId: string): string {
  return `/projects/${projectId}/claude-md-suggestions`
}
